/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AngleDownIcon } from "./ProductDropdown";
import type { NavDropdownSection } from "./types";

interface NavDropdownProps {
  sections: NavDropdownSection[];
  selectedLanguage: string;
  onNavClick: () => void;
}

/**
 * S-1: Window in milliseconds during which a click immediately following a
 * hover-open is treated as part of the same gesture and the toggle is
 * suppressed. Covers two failure modes the existing touchInProgress guard
 * does not catch:
 *   1. Capacitive proximity sensing on Surface-class hybrids — pointerenter
 *      fires before any touchstart, so touchInProgress is false when the
 *      hover-open path runs; the subsequent click would otherwise toggle the
 *      just-opened dropdown closed.
 *   2. Touchpad-only tap on touchscreen-capable laptops — pointer events
 *      report pointerType="mouse" with NO touch event firing at all, so the
 *      touchInProgress guard never engages.
 *
 * 250ms is well above the 95th-percentile tap-gesture duration on Surface-
 * class hardware (~80–180ms) and well below the floor of a deliberate mouse
 * hover-then-click (400–600ms), so genuine mouse-click-to-dismiss is
 * unaffected. Keyboard and focus paths use separate handlers — also
 * unaffected.
 */
const HOVER_CLICK_DEBOUNCE_MS = 250;

/**
 * Returns true only for mouse or pen pointer types.
 * Filters out touch events so onPointerEnter/Leave only open the dropdown
 * via hover-capable inputs on Chrome 133+ and Firefox on Windows touch-capable
 * devices (where onMouseEnter can be suppressed due to pointer type
 * misclassification — Chromium bug, confirmed Feb 2025).
 */
const isHoverCapable = (e: React.PointerEvent): boolean =>
  e.pointerType === "mouse" || e.pointerType === "pen";

const NavDropdown: React.FC<NavDropdownProps> = ({
  sections,
  // selectedLanguage is retained in the interface so Header.tsx requires no
  // change, but the forum-link fix (DD-1, Session 107) removed the only use
  // of this prop inside NavDropdown — every locale now renders the forum as
  // an external <a> link via VITE_REACT_APP_SUPPORT_FORUM_URL.
  onNavClick,
}) => {
  const { t } = useTranslation();
  const touchInProgressMap = useRef<Record<string, boolean>>({});
  // S-1: per-section hover-open timestamps (keyed by section title).
  const lastHoverOpenAtMap = useRef<Record<string, number>>({});

  return (
    <>
      {sections.map(({ title, openState, setOpenState, links }) => (
        <div
          key={title}
          className="items product-container header-item"
          role="button"
          tabIndex={0}
          onTouchStart={() => {
            touchInProgressMap.current[title] = true;
          }}
          onClick={() => {
            // S-1: Suppress the toggle-to-close that would cancel a
            // hover-open when a misclassified touch tap (proximity-sensing
            // or touchpad-only) fires pointerenter before the click.
            const openedAt = lastHoverOpenAtMap.current[title] ?? 0;
            const withinDebounce =
              Date.now() - openedAt < HOVER_CLICK_DEBOUNCE_MS;
            if (!withinDebounce) {
              setOpenState(!openState);
            }
            if (touchInProgressMap.current[title]) {
              setTimeout(() => {
                touchInProgressMap.current[title] = false;
              }, 300);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpenState(!openState);
            } else if (e.key === "Escape" && openState) {
              e.preventDefault();
              setOpenState(false);
            }
          }}
          onPointerEnter={(e) => {
            if (isHoverCapable(e) && !touchInProgressMap.current[title]) {
              lastHoverOpenAtMap.current[title] = Date.now();
              setOpenState(true);
            }
          }}
          onPointerLeave={(e) => {
            if (isHoverCapable(e) && !touchInProgressMap.current[title])
              setOpenState(false);
          }}
          onFocus={() => {
            // Issue 3: a tap fires focus then click; without this guard the
            // focus opens the dropdown and the click toggles it closed, so it
            // only opens on the second tap. The flag is set only during a touch
            // gesture, so keyboard Tab focus is unaffected.
            if (!touchInProgressMap.current[title]) setOpenState(true);
          }}
          onBlur={(e) => {
            // Only close if focus truly left the entire container, not just
            // moved to a child element inside the dropdown panel.
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setOpenState(false);
            }
          }}
          aria-haspopup="true"
          aria-expanded={openState}
        >
          <span>{t(`header.${title}.title`)}</span>
          <AngleDownIcon />
          {openState && (
            <div className="items-dropdown">
              <div className="support-list">
                {links.map(
                  (
                    { to, titleKey, descKey, isExternal, isDjangoPage },
                    index,
                  ) => {
                    const isExternalLink = isExternal === true;
                    const isDjangoPageLink = isDjangoPage === true;

                    return (
                      <React.Fragment key={to}>
                        {isExternalLink ? (
                          <a
                            className="product-detail"
                            href={to}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onNavClick}
                          >
                            {/* WCAG SC 1.3.1: product name is styled nav text, not a content heading */}
                            <span className="product-name">{t(titleKey)}</span>
                            <p className="product-description">{t(descKey)}</p>
                          </a>
                        ) : isDjangoPageLink ? (
                          <a
                            className="product-detail"
                            href={to}
                            onClick={onNavClick}
                          >
                            {/* WCAG SC 1.3.1: product name is styled nav text, not a content heading */}
                            <span className="product-name">{t(titleKey)}</span>
                            <p className="product-description">{t(descKey)}</p>
                          </a>
                        ) : (
                          <Link
                            className="product-detail"
                            to={to}
                            onClick={onNavClick}
                          >
                            {/* WCAG SC 1.3.1: product name is styled nav text, not a content heading */}
                            <span className="product-name">{t(titleKey)}</span>
                            <p className="product-description">{t(descKey)}</p>
                          </Link>
                        )}
                        {index < links.length - 1 && <hr />}
                      </React.Fragment>
                    );
                  },
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </>
  );
};

export default NavDropdown;
